import { useState } from "react";
import { useNavigate } from "@/router";
import type { SortingState } from "@tanstack/react-table";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import MetricGrid from "@/components/MetricGrid";
import Table, { TableControls, TableFooter, TableHeading } from "@/components/Table";
import Select from "@/components/Select";
import SearchInput from "@/components/SearchInput";
import { buildTagColumn } from "@/components/Table/buildTagColumn";
import type { TagType } from "@/components/Tag/Tag";
import { capitalize } from "lodash";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { toFormat } from "@/lib/dateHelpers";
import type { Path } from "@/router";
import { GET_PERSON_BENEFICIARIES_QUERY, type BeneficiaryRecord } from "../../person/queries";
import { useCanManageBeneficiariesBool } from "@/hooks/useCapabilities";
import { updateBeneficiaryApplicationTypes } from "./_components/beneficiaryFormUtils";
import CcmsAssociateBeneficiariesTable from "../_components/CcmsAssociateBeneficiariesTable";
import CcmsMemberInsuranceTable from "../_components/CcmsMemberInsuranceTable";
import { exportBeneficiariesCsv } from "./_components/exportBeneficiariesCsv";

interface BeneficiaryRow {
  id: string;
  fullName: string;
  beneficiaryType: string;
  relationship: string;
  percent: string | null;
  dateOfBirth: string | null;
  ssnLast4: string;
  applicationType: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  status: string;
}

function formatRows(records: BeneficiaryRecord[]): BeneficiaryRow[] {
  return records.map((r) => ({
    id: r.id,
    fullName: r.fullName,
    beneficiaryType: r.beneficiaryType,
    relationship: r.relationship,
    percent: r.percent != null ? `${r.percent}%` : null,
    dateOfBirth: toFormat(r.dateOfBirth),
    ssnLast4: r.ssnLast4,
    applicationType: r.applicationType,
    effectiveStartDate: r.effectiveStartDate,
    effectiveEndDate: r.effectiveEndDate ?? null,
    status: r.effectiveEndDate ? "Closed" : "Active"
  }));
}

export default function BeneficiariesTab() {
  const { pgaId } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "Active" | "Closed">("Active");
  const [typeFilter, setTypeFilter] = useState<"all" | "primary" | "contingent">("all");
  const [programTypeFilter, setProgramTypeFilter] = useState<"all" | "member" | "associate">("all");
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "effectiveEndDate", desc: true }]);
  const navigate = useNavigate();
  const canManageBeneficiaries = useCanManageBeneficiariesBool();

  const { data, loading, error } = useQuery(GET_PERSON_BENEFICIARIES_QUERY, {
    variables: { pgaId: pgaId!, includeInactive: true },
    fetchPolicy: "cache-first",
    skip: !pgaId
  });

  if (error) {
    return (
      <ErrorBoundary variant="inline" title="Failed to Load Beneficiaries" message={error.message} className="mt-12" />
    );
  }

  const allRecords = data?.person?.beneficiaries ?? [];
  const activeRecords = allRecords.filter((r) => !r.effectiveEndDate);
  const inactiveRecords = allRecords.filter((r) => !!r.effectiveEndDate);
  const updateApplicationTypes = updateBeneficiaryApplicationTypes(
    data?.person?.latestProgram?.programType?.name,
    activeRecords
  );
  const statusFiltered =
    statusFilter === "Active" ? activeRecords : statusFilter === "Closed" ? inactiveRecords : allRecords;
  const filteredRecords = statusFiltered
    .filter((r) => !searchQuery || r.fullName?.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((r) => typeFilter === "all" || r.beneficiaryType === typeFilter)
    .filter((r) => programTypeFilter === "all" || r.applicationType === programTypeFilter);
  const displayedRows = formatRows(filteredRecords);
  const programTypeName = data?.person?.latestProgram?.programType?.name?.toLowerCase();

  const activeFilterCount = [statusFilter !== "all", typeFilter !== "all", programTypeFilter !== "all"].filter(
    Boolean
  ).length;

  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setTypeFilter("all");
    setProgramTypeFilter("all");
  };

  const metrics: MetricCardType<Path>[] = [
    {
      label: "Active Beneficiaries",
      value: loading ? "—" : activeRecords.length.toString(),
      iconName: "person"
    },
    {
      label: "Closed Beneficiaries",
      value: loading ? "—" : inactiveRecords.length.toString(),
      iconName: "person_cancel"
    }
  ];

  return (
    <>
      <MetricGrid metrics={metrics} layout="stacked" />

      <Table
        renderDetails={() => {
          const headingActions = canManageBeneficiaries
            ? [
                ...updateApplicationTypes.map((applicationType) => ({
                  label:
                    updateApplicationTypes.length > 1
                      ? `Update ${capitalize(applicationType)} Beneficiaries`
                      : "Update Beneficiaries",
                  onClick: () => {
                    navigate("/people/:pgaId/benefits/beneficiaries/update/:applicationType", {
                      params: { pgaId: pgaId!, applicationType }
                    });
                  },
                  testId:
                    updateApplicationTypes.length > 1
                      ? `update-${applicationType}-beneficiaries`
                      : "update-beneficiaries",
                  disabled: loading || !data?.person?.id
                })),
                {
                  label: "Export",
                  variant: "outlined" as const,
                  icon: "download" as const,
                  onClick: () => {
                    if (!pgaId || !data?.person) return;
                    exportBeneficiariesCsv({
                      pgaId,
                      beneficiaries: allRecords,
                      ccmsAssociateBeneficiaries: data.person.ccmsAssociateBeneficiaries ?? [],
                      ccmsMemberInsuranceRecords: data.person.ccmsMemberInsuranceRecords ?? []
                    });
                  },
                  testId: "export-beneficiaries",
                  disabled: loading || !data?.person?.id
                }
              ]
            : [];

          return headingActions.length > 0 ? (
            <TableHeading topSpacing="compact" actions={headingActions} />
          ) : (
            <div className="pt-6" />
          );
        }}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            useExpandedFilters={true}
            expandedFiltersState={{
              status: statusFilter !== "all",
              type: typeFilter !== "all",
              programType: programTypeFilter !== "all"
            }}
            onClearAllFilters={clearAllFilters}
            onSortClick={onSortClick}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Beneficiaries"
              query={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery("")}
            />
            <div data-name="status">
              <Select
                label="Status"
                options={[
                  { value: "all", label: "Show All" },
                  { value: "Active", label: "Active" },
                  { value: "Closed", label: "Closed" }
                ]}
                value={statusFilter}
                onSelect={(value) => setStatusFilter(value as "all" | "Active" | "Closed")}
              />
            </div>
            <div data-name="type">
              <Select
                label="Type"
                options={[
                  { value: "all", label: "Show All" },
                  { value: "primary", label: "Primary" },
                  { value: "contingent", label: "Contingent" }
                ]}
                value={typeFilter}
                onSelect={(value) => setTypeFilter(value as "all" | "primary" | "contingent")}
              />
            </div>
            <div data-name="programType">
              <Select
                label="Program Type"
                options={[
                  { value: "all", label: "Show All" },
                  { value: "member", label: "Member" },
                  { value: "associate", label: "Associate" }
                ]}
                value={programTypeFilter}
                onSelect={(value) => setProgramTypeFilter(value as "all" | "member" | "associate")}
              />
            </div>
          </TableControls>
        )}
        data={displayedRows}
        loading={loading}
        hasActiveFilters={
          allRecords.length > 0 &&
          (!!searchQuery || statusFilter !== "all" || typeFilter !== "all" || programTypeFilter !== "all")
        }
        sortingState={sortingState}
        onSortingChange={setSortingState}
        columns={[
          {
            header: "Name",
            id: "fullName",
            accessorKey: "fullName",
            enableSorting: true,
            size: 280,
            minSize: 220,
            meta: { sortType: "text", sortLabel: "Name" }
          },
          {
            header: "Type",
            id: "beneficiaryType",
            accessorKey: "beneficiaryType",
            enableSorting: false,
            size: 100,
            minSize: 88,
            cell: ({ row }) => capitalize(row.original.beneficiaryType)
          },
          {
            header: "Relationship",
            id: "relationship",
            accessorKey: "relationship",
            enableSorting: false
          },
          {
            header: "Percent",
            id: "percent",
            accessorKey: "percent",
            enableSorting: false,
            size: 72,
            minSize: 64
          },
          {
            header: "Date of Birth",
            id: "dateOfBirth",
            accessorKey: "dateOfBirth",
            enableSorting: false
          },
          {
            header: "SSN Last 4",
            id: "ssnLast4",
            accessorKey: "ssnLast4",
            enableSorting: false,
            size: 96,
            minSize: 88,
            cell: ({ row }) => row.original.ssnLast4 || "—"
          },
          {
            header: "Program Type",
            id: "applicationType",
            accessorKey: "applicationType",
            enableSorting: false,
            cell: ({ row }) => capitalize(row.original.applicationType)
          },
          {
            header: "Effective Start",
            id: "effectiveStartDate",
            accessorKey: "effectiveStartDate",
            enableSorting: true,
            meta: { sortType: "date", sortLabel: "Effective Start" },
            cell: ({ row }) => toFormat(row.original.effectiveStartDate) ?? "—"
          },
          {
            header: "Effective End",
            id: "effectiveEndDate",
            accessorKey: "effectiveEndDate",
            enableSorting: true,
            meta: { sortType: "date", sortLabel: "Effective End" },
            sortingFn: (rowA, rowB) => {
              const a = rowA.original.effectiveEndDate;
              const b = rowB.original.effectiveEndDate;
              if (!a && !b) return 0;
              if (!a) return 1;
              if (!b) return -1;
              return a < b ? -1 : a > b ? 1 : 0;
            },
            cell: ({ row }) => (row.original.effectiveEndDate ? toFormat(row.original.effectiveEndDate) : "—")
          },
          buildTagColumn<BeneficiaryRow>({
            header: "Status",
            accessorKey: "status",
            getTagLabel: (row) => row.status,
            getTagType: (row): TagType => (row.status === "Active" ? "success" : "surface")
          })
        ]}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Beneficiaries"
            caption="No beneficiary designations have been recorded for this person."
            iconName="person"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={displayedRows.length} />}
        data-testid="beneficiaries-table"
      />
      {programTypeName === "associate" && (
        <CcmsAssociateBeneficiariesTable records={data?.person?.ccmsAssociateBeneficiaries ?? []} loading={loading} />
      )}
      {programTypeName === "member" && (
        <CcmsMemberInsuranceTable records={data?.person?.ccmsMemberInsuranceRecords ?? []} loading={loading} />
      )}
    </>
  );
}
